import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { environment } from 'environments/environment';
import { MatToolbarModule } from '@angular/material/toolbar';
import { UserAccountDto } from '@api/client';
import { accountHomeUrl } from '@shared/guards/account-view.guard';
import { isStrategyAccount } from '@shared/store/features/current-user.utils';
import { LogoComponent } from '@shared/components/logo/logo.component';

import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { UserProfileComponent } from '../user-profile/user-profile.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOptgroup, MatOption, MatSelect } from '@angular/material/select';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { asOptional } from '@stillmandigital/common';
import {
  SupportedTimezone,
  supportedTimezones,
} from '@shared/services/datetime/datetime.utils';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateTimeService } from '@shared/services/datetime/datetime.service';
import { TranslateTimezonePipe } from '@shared/pipes/translate-timezone.pipe';
import { Router } from '@angular/router';
import { SharedStore } from '@shared/store/shared.signal-store';

interface AccountGroup {
  label: string;
  accounts: UserAccountDto[];
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatToolbarModule,
    LogoComponent,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    UserProfileComponent,
    TranslateModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    TranslateTimezonePipe,
    MatOptgroup,
  ],
})
export class NavbarComponent implements OnInit {
  public readonly isSandbox = !environment.production;

  public readonly supportedTimezones = supportedTimezones;

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _datetimeService = inject(DateTimeService);

  private readonly _sharedStore = inject(SharedStore);

  private readonly _router = inject(Router);

  public readonly accounts = this._sharedStore.accounts;

  public readonly activeAccountId = this._sharedStore.activeAccountId;

  // A strategy subaccount opens a different view from every other account, so once the user has
  // both kinds the selector labels them; with one kind there is nothing to tell apart.
  public readonly accountGroups = computed<AccountGroup[] | null>(() => {
    const strategyAccounts = this.accounts().filter(isStrategyAccount);

    if (!strategyAccounts.length) {
      return null;
    }

    return [
      {
        label: 'NAVBAR.OTC',
        accounts: this.accounts().filter(account => !isStrategyAccount(account)),
      },
      { label: 'NAVBAR.STRATEGIES', accounts: strategyAccounts },
    ];
  });

  public readonly timezoneControl =
    inject(NonNullableFormBuilder).control(asOptional<SupportedTimezone>());

  public selectAccount(accountId: number) {
    this._sharedStore.selectAccount(accountId);

    // Each account type has exactly one view, so the new account's view is where the user has to
    // end up; the route guards only cover entering a view by URL.
    this._router.navigateByUrl(
      accountHomeUrl(this._sharedStore.isStrategyAccountActive()),
    );
  }

  public ngOnInit() {
    this._datetimeService.timezone$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(timezone => this.timezoneControl.reset(timezone, { emitEvent: false }));

    this.timezoneControl.valueChanges
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe(timezone => this._datetimeService.setTimezone(timezone));
  }
}
